import { useCallback, useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useNavigate, useParams } from '../router';

import { api, ApiError } from '../api';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { Notice } from '../components/Notice';
import type { Conversation, Device, Message } from '../types';
import { activeDevices, displayAddress, shouldSubmitComposer } from '../types';

interface ReplyAttempt { recipient: string; body: string; idempotencyKey: string }
interface MessageCursor { before: string; beforeId: string }

const messageStatusPresentation: Record<string, { label: string; icon: string }> = {
  delivered: { label: 'Delivered', icon: 'done_all' },
  sent: { label: 'Sent', icon: 'done' },
  sending: { label: 'Sending', icon: 'arrow_upward' },
  queued: { label: 'Waiting for the phone', icon: 'schedule' },
  failed: { label: 'Not sent', icon: 'error' },
  canceled: { label: 'Canceled', icon: 'cancel' },
};

function MessageStatus({ status }: { status: string }) {
  const normalized = status.toLowerCase();
  const { label, icon } = messageStatusPresentation[normalized] ?? messageStatusPresentation.queued!;
  return <span className={`message-status status-${normalized}`} title={label} aria-label={label}><Icon name={icon} size={15} /></span>;
}

export function ConversationPage() {
  const { conversationId = '' } = useParams();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [nextCursor, setNextCursor] = useState<MessageCursor | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const replyAttempt = useRef<ReplyAttempt | null>(null);
  const messageScroll = useRef<HTMLDivElement>(null);
  const loadedOlder = useRef(false);
  const initialScrollComplete = useRef(false);
  const activeConversationId = useRef(conversationId);
  const routeGeneration = useRef(0);
  const latestLoadRequest = useRef(0);
  const olderRequestSequence = useRef(0);
  const olderRequestInFlight = useRef(false);
  activeConversationId.current = conversationId;
  const hasActiveDevice = activeDevices(devices).length > 0;

  const load = useCallback(async (refreshDevices = true) => {
    const requestedConversationId = conversationId;
    const requestedGeneration = routeGeneration.current;
    const requestId = ++latestLoadRequest.current;
    const isCurrent = () => activeConversationId.current === requestedConversationId
      && routeGeneration.current === requestedGeneration
      && latestLoadRequest.current === requestId;
    try {
      const [thread, deviceResult] = await Promise.all([
        api<{ conversation: Conversation; messages: Message[]; nextCursor: MessageCursor | null }>(`/api/conversations/${requestedConversationId}/messages`),
        refreshDevices ? api<{ devices: Device[] }>('/api/devices') : Promise.resolve(undefined),
      ]);
      if (!isCurrent()) return;
      setConversation(thread.conversation);
      setMessages((current) => loadedOlder.current ? [...new Map([...current, ...thread.messages].map((message) => [message.id, message])).values()].sort((a, b) => new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime()) : thread.messages);
      if (!loadedOlder.current) setNextCursor(thread.nextCursor);
      if (!initialScrollComplete.current) {
        window.requestAnimationFrame(() => {
          if (!isCurrent()) return;
          if (messageScroll.current) messageScroll.current.scrollTop = messageScroll.current.scrollHeight;
          initialScrollComplete.current = true;
        });
      }
      if (deviceResult) {
        setDevices(deviceResult.devices);
      }
      if (thread.conversation.unread_count > 0) await api(`/api/conversations/${requestedConversationId}`, { method: 'PATCH', body: JSON.stringify({ markRead: true }) });
      if (isCurrent()) setLoadError('');
    } catch (caught) {
      if (isCurrent()) setLoadError(caught instanceof ApiError ? caught.message : 'Could not load this conversation.');
    }
  }, [conversationId]);

  useEffect(() => {
    routeGeneration.current += 1;
    latestLoadRequest.current += 1;
    olderRequestSequence.current += 1;
    olderRequestInFlight.current = false;
    loadedOlder.current = false;
    initialScrollComplete.current = false;
    setConversation(null);
    setMessages([]);
    setNextCursor(null);
    setLoadingOlder(false);
    setError('');
    setLoadError('');
    let pollInFlight: Promise<void> | null = null;
    const runLoad = (refreshDevices: boolean) => {
      if (pollInFlight) return;
      pollInFlight = load(refreshDevices).finally(() => { pollInFlight = null; });
    };
    runLoad(true);
    const poll = () => {
      if (document.visibilityState !== 'visible' || pollInFlight) return;
      runLoad(false);
    };
    const timer = window.setInterval(poll, 5_000);
    document.addEventListener('visibilitychange', poll);
    return () => {
      routeGeneration.current += 1;
      latestLoadRequest.current += 1;
      olderRequestSequence.current += 1;
      olderRequestInFlight.current = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [load]);

  async function loadOlderMessages() {
    if (!nextCursor || olderRequestInFlight.current) return;
    const requestedConversationId = conversationId;
    const requestedGeneration = routeGeneration.current;
    const requestId = ++olderRequestSequence.current;
    const isCurrent = () => activeConversationId.current === requestedConversationId
      && routeGeneration.current === requestedGeneration
      && olderRequestSequence.current === requestId;
    olderRequestInFlight.current = true;
    const scroller = messageScroll.current;
    const previousHeight = scroller?.scrollHeight ?? 0;
    setLoadingOlder(true); setError('');
    try {
      const query = new URLSearchParams({ before: nextCursor.before, beforeId: nextCursor.beforeId, limit: '100' });
      const page = await api<{ messages: Message[]; nextCursor: MessageCursor | null }>(`/api/conversations/${requestedConversationId}/messages?${query}`);
      if (!isCurrent()) return;
      loadedOlder.current = true;
      setMessages((current) => [...page.messages.filter((message) => !current.some((existing) => existing.id === message.id)), ...current]);
      setNextCursor(page.nextCursor);
      window.requestAnimationFrame(() => {
        if (!isCurrent()) return;
        if (scroller) scroller.scrollTop += scroller.scrollHeight - previousHeight;
      });
    } catch (caught) {
      if (isCurrent()) setError(caught instanceof ApiError ? caught.message : 'Could not load older messages.');
    } finally {
      if (isCurrent()) {
        olderRequestInFlight.current = false;
        setLoadingOlder(false);
      }
    }
  }

  async function update(change: Partial<Pick<Conversation, 'archived' | 'pinned' | 'blocked'>> & { trashed?: boolean }) {
    if (!conversation) return;
    setBusy(true);
    try {
      await api(`/api/conversations/${conversationId}`, { method: 'PATCH', body: JSON.stringify(change) });
      setConversation({
        ...conversation,
        ...change,
        archived: change.trashed ? false : change.archived ?? conversation.archived,
        trashed_at: change.trashed === true ? new Date().toISOString() : change.trashed === false || change.archived ? null : conversation.trashed_at,
      });
    } catch (caught) { setError(caught instanceof ApiError ? caught.message : 'Could not update the conversation.'); }
    finally { setBusy(false); }
  }

  async function deletePermanently() {
    setBusy(true); setError('');
    try {
      await api(`/api/conversations/${conversationId}`, { method: 'DELETE' });
      navigate('/app/inbox', { replace: true });
    } catch (caught) { setError(caught instanceof ApiError ? caught.message : 'Could not delete this conversation.'); }
    finally { setBusy(false); setShowDelete(false); }
  }

  async function reply(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!conversation) return;
    if (!hasActiveDevice) { setError('Connect a phone and enable sync before sending from web.'); return; }
    const form = event.currentTarget;
    const data = new FormData(form);
    const body = String(data.get('body'));
    const attempt = replyAttempt.current && replyAttempt.current.recipient === conversation.normalized_address && replyAttempt.current.body === body
      ? replyAttempt.current
      : { recipient: conversation.normalized_address, body, idempotencyKey: crypto.randomUUID() };
    replyAttempt.current = attempt;
    setBusy(true); setError('');
    try {
      await api('/api/v1/messages', { method: 'POST', headers: { 'Idempotency-Key': attempt.idempotencyKey }, body: JSON.stringify({ to: attempt.recipient, message: attempt.body }) });
      replyAttempt.current = null; form.reset(); await load();
    } catch (caught) { setError(caught instanceof ApiError ? caught.message : 'Could not queue this message.'); }
    finally { setBusy(false); }
  }

  function sendOnEnter(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (!shouldSubmitComposer(event.key, event.shiftKey, event.nativeEvent.isComposing)) return;
    event.preventDefault();
    event.currentTarget.form?.requestSubmit();
  }

  if (loadError && !conversation) return <div className="page"><Notice kind="error">{loadError}</Notice><Link className="button button-quiet" to="/app/inbox">Back to messages</Link></div>;
  if (!conversation) return <div className="screen-loader"><span className="loading-dot" /></div>;
  return (
    <div className="thread-page">
      <header className="thread-header">
        <button className="icon-button" type="button" aria-label="Back" onClick={() => navigate('/app/inbox')}><Icon name="arrow_back" /></button>
        <span className="avatar">{displayAddress(conversation).slice(0, 1).toUpperCase()}</span>
        <div><h1>{displayAddress(conversation)}</h1><small>{conversation.normalized_address}</small></div>
        <div className="thread-actions">
          {conversation.trashed_at ? <>
            <button className="icon-button" disabled={busy} type="button" aria-label="Restore to inbox" onClick={() => void update({ trashed: false })}><Icon name="restore_from_trash" /></button>
            <button className="icon-button danger" disabled={busy} type="button" aria-label="Delete permanently" onClick={() => setShowDelete(true)}><Icon name="delete_forever" /></button>
          </> : <>
            <button className="icon-button" disabled={busy} type="button" aria-label={conversation.pinned ? 'Unpin' : 'Pin'} onClick={() => void update({ pinned: !conversation.pinned })}><Icon name="keep" filled={conversation.pinned} /></button>
            <button className="icon-button" disabled={busy} type="button" aria-label={conversation.archived ? 'Restore' : 'Archive'} onClick={() => void update({ archived: !conversation.archived })}><Icon name={conversation.archived ? 'unarchive' : 'archive'} /></button>
            <button className="icon-button" disabled={busy} type="button" aria-label="Move to trash" onClick={() => void update({ trashed: true })}><Icon name="delete" /></button>
            <button className={`icon-button ${conversation.blocked ? 'danger' : ''}`} disabled={busy} type="button" aria-label={conversation.blocked ? 'Unblock' : 'Block'} onClick={() => void update({ blocked: !conversation.blocked })}><Icon name="block" /></button>
          </>}
        </div>
      </header>
      <div className="message-scroll" ref={messageScroll}>
        {(error || loadError) && <Notice kind="error">{error || loadError}</Notice>}
        {nextCursor && <div className="load-older-messages"><button className="button button-tonal" type="button" disabled={loadingOlder} onClick={() => void loadOlderMessages()}>{loadingOlder ? 'Loading…' : 'Load older messages'}</button></div>}
        {messages.length === 0 ? <EmptyState icon="chat" title="No synced messages" body="Messages will appear when your phone finishes syncing." /> : messages.map((message) => (
          <article className={`message-bubble ${message.direction}`} key={message.id}>
            <p>{message.body}</p>
            <small>{new Date(message.occurred_at).toLocaleString()} {message.direction === 'outgoing' && <MessageStatus status={message.status} />}</small>
          </article>
        ))}
      </div>
      {conversation.trashed_at ? <div className="trashed-thread-bar"><Icon name="delete" /><span>This server copy is in Trash and will be deleted after 30 days.</span><button className="button button-tonal" disabled={busy} type="button" onClick={() => void update({ trashed: false })}>Restore</button></div> : <form className="reply-bar" onSubmit={(event) => void reply(event)}>
        <div className="reply-fields">
          <small>Active phone · Automatic SIM</small>
          <label><span className="sr-only">Message</span><textarea name="body" required maxLength={10_000} rows={1} placeholder={conversation.blocked ? 'Unblock this conversation to reply' : 'Write a message'} disabled={conversation.blocked || busy} onKeyDown={sendOnEnter} /></label>
        </div>
        <button className="send-button" type="submit" disabled={conversation.blocked || busy} aria-label="Send message"><Icon name="send" filled /></button>
      </form>}
      {showDelete && <Modal title="Delete this synced conversation?" description="This permanently removes the conversation from the server. SMS stored locally on your Android phone is not deleted." onClose={() => setShowDelete(false)}><div className="modal-actions"><button className="button button-quiet" type="button" onClick={() => setShowDelete(false)}>Keep in Trash</button><button className="button button-danger" disabled={busy} type="button" onClick={() => void deletePermanently()}>Delete permanently</button></div></Modal>}
    </div>
  );
}
